import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState(location.state?.message || '');
  const [isLoading, setIsLoading] = useState(false);

  // Get redirect path from localStorage or location state, fallback to /inventory
  const redirectPath = React.useMemo(() => {
    // Try localStorage first
    const storedPath = localStorage.getItem('redirectPath');
    if (storedPath) {
      // Ensure it's a full path starting with /
      return storedPath.startsWith('/') ? storedPath : `/${storedPath}`;
    }
    
    // Then try location state
    const locationPath = location.state?.from?.pathname;
    if (locationPath) {
      return locationPath.startsWith('/') ? locationPath : `/${locationPath}`;
    }
    
    // Default to /inventory
    return '/inventory';
  }, [location.state?.from?.pathname]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Clear any existing CSRF errors from the DOM
      const errorDisplay = document.getElementById('error-display-container');
      if (errorDisplay) {
        document.body.removeChild(errorDisplay);
      }
      
      // Attempt login
      await login(email, password);
      
      // Get the final path, ensuring it starts with /
      const storedPath = localStorage.getItem('redirectPath');
      
      // Determine the final path, but never redirect back to login or register
      let finalPath = storedPath || redirectPath;
      
      // If the path is /login or /register, redirect to /inventory instead
      if (finalPath === '/login' || finalPath === '/register' || 
          finalPath.startsWith('/login') || finalPath.startsWith('/register')) {
        finalPath = '/inventory';
      }
      
      const normalizedPath = finalPath.startsWith('/') ? finalPath : `/${finalPath}`;
      
      // Clear stored path
      localStorage.removeItem('redirectPath');
      
      // Add a small delay before navigation
      setTimeout(() => {
        // Navigate to the saved path
        navigate(normalizedPath, { replace: true });
      }, 100);
    } catch (err) {
      console.error('Login error:', err);
      
      // Check if it's a CSRF error
      const isCSRFError = err?.response?.status === 403 && 
        (err?.response?.data?.error?.includes('CSRF') || 
         err?.message?.includes('CSRF'));
      
      if (isCSRFError) {
        setError('Session error. Please try again.');
      } else {
        setError('Failed to log in. Please check your credentials.');
      }
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-indigo-600">NumisNavigator</h1>
          <div className="mt-4 space-y-4">
            <p className="text-2xl text-gray-600">
              Your comprehensive coin management solution, designed for both dealers and collectors.
            </p>
            <div className="space-y-2 text-lg text-gray-500">
              <p>• Track and manage your entire coin inventory</p>
              <p>• Automatic TPG submission tracking</p>
              <p>• Record sales transacton and manage clients</p>
              <p>• Plan auction bids and conduct market research</p>
            </div>
          </div>
        </div>
        <h2 className="mt-6 text-center text-2xl font-extrabold text-gray-900">
          Sign in to your account
        </h2>
        <form className="mt-6 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Password"
              />
            </div>
          </div>

          {message && (
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">{message}</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {isLoading ? (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : null}
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="w-full flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Create an account
            </button>
            <div className="text-center mt-2">
              <Link to="/forgot-password" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                Forgot your password?
              </Link>
            </div>
          </div>
        </form>
        <div className="text-center text-sm text-gray-500">
          <Link to="/privacy-policy" className="hover:text-indigo-500">Privacy Policy</Link>
          <span className="mx-2">•</span>
          <Link to="/terms-of-service" className="hover:text-indigo-500">Terms of Service</Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
